import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerBranchToken, CustomerCouponToken, CustomerReferralsDto } from '@sleed/bizcommerce-api';
import { customerBranchesApi, customerCouponsApi, referralsApi } from '~/lib/api/client';

/* -------------------------------------------------------------- branches (per customer) */

const branchKeys = (cid: number) => ['customer-branches', cid] as const;

export function useCustomerBranches(customerId: number | null) {
	return useQuery<CustomerBranchToken[]>({
		queryKey: branchKeys(customerId ?? 0),
		queryFn: async () => (await customerBranchesApi.getByCustomer(customerId as number)).data.items ?? [],
		enabled: customerId != null && customerId > 0
	});
}

export function useCustomerBranchMutations(customerId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: branchKeys(customerId) });
	const create = useMutation({
		mutationFn: async (token: CustomerBranchToken) => (await customerBranchesApi.insert({ ...token, cid: customerId })).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: CustomerBranchToken) => (await customerBranchesApi.update({ ...token, cid: customerId })).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerBranchesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

/* -------------------------------------------------------------- coupons (per customer) */

const couponKeys = (cid: number) => ['customer-coupons', cid] as const;

export function useCustomerCoupons(customerId: number | null) {
	return useQuery<CustomerCouponToken[]>({
		queryKey: couponKeys(customerId ?? 0),
		queryFn: async () => (await customerCouponsApi.getByCustomer(customerId as number)).data.items ?? [],
		enabled: customerId != null && customerId > 0
	});
}

export function useCustomerCouponMutations(customerId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: couponKeys(customerId) });
	const create = useMutation({
		mutationFn: async (token: CustomerCouponToken) => (await customerCouponsApi.insert({ ...token, cid: customerId })).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: CustomerCouponToken) => (await customerCouponsApi.update({ ...token, cid: customerId })).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerCouponsApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

/* -------------------------------------------------------------- referrals (per customer, BW-8460) */

const referralKeys = (cid: number) => ['customer-referrals', cid] as const;

/** The customer's referral code, who referred them and whom they referred (a read never creates a code). */
export function useCustomerReferrals(customerId: number | null) {
	return useQuery<CustomerReferralsDto>({
		queryKey: referralKeys(customerId ?? 0),
		queryFn: async () => (await referralsApi.getCustomerReferrals(customerId as number)).data,
		enabled: customerId != null && customerId > 0
	});
}

/** A new code for the customer; links already sent with the old one stop applying. */
export function useRegenerateReferralCode(customerId: number) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => (await referralsApi.regenerateCode(customerId)).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: referralKeys(customerId) })
	});
}
